import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import {
  approveDisplayDevice,
  broadcastDisplayAppUpdate,
  listDisplayDevices,
  rejectDisplayDevice,
  revokeDisplayDevice,
  updateDisplayDevice,
  type DisplayDevice,
  type DisplayThemeMode,
} from "@/lib/montageplanDisplay";
import {
  DISPLAY_UI_SCALE_MAX,
  DISPLAY_UI_SCALE_MIN,
  DISPLAY_UI_SCALE_PRESETS,
  normalizeDisplayUiScale,
} from "@/pages/montageplan/displayUiScale";

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusLabel(status: DisplayDevice["status"]): string {
  switch (status) {
    case "pending":
      return "Ausstehend";
    case "approved":
      return "Freigegeben";
    case "rejected":
      return "Abgelehnt";
    case "revoked":
      return "Widerrufen";
    default:
      return status;
  }
}

const THEME_OPTIONS: { value: DisplayThemeMode; label: string }[] = [
  { value: "dark", label: "Dunkel" },
  { value: "light", label: "Hell" },
  { value: "system", label: "System" },
];

export function DisplayDevicesSection() {
  const [items, setItems] = useState<DisplayDevice[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [themeBusyId, setThemeBusyId] = useState<number | null>(null);
  const [scaleBusyId, setScaleBusyId] = useState<number | null>(null);
  const [broadcastBusy, setBroadcastBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await listDisplayDevices();
      setItems(res.items);
      setPendingCount(res.pending_count);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Geräte konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => {
      // Versteckter Tab: kein Hintergrund-Polling (Serverlast).
      if (document.visibilityState === "hidden") return;
      void load();
    }, 15000);
    return () => window.clearInterval(timer);
  }, [load]);

  async function runAction(id: number, action: "approve" | "reject" | "revoke") {
    setBusyId(id);
    try {
      if (action === "approve") await approveDisplayDevice(id);
      else if (action === "reject") await rejectDisplayDevice(id);
      else await revokeDisplayDevice(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Aktion fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  }

  async function changeTheme(id: number, theme_mode: DisplayThemeMode) {
    setThemeBusyId(id);
    try {
      await updateDisplayDevice(id, { theme_mode });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Theme konnte nicht gespeichert werden.");
    } finally {
      setThemeBusyId(null);
    }
  }

  async function changeScale(id: number, ui_scale: number) {
    setScaleBusyId(id);
    try {
      await updateDisplayDevice(id, { ui_scale: normalizeDisplayUiScale(ui_scale) });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Größe konnte nicht gespeichert werden.");
    } finally {
      setScaleBusyId(null);
    }
  }

  return (
    <section className="rounded-ios-lg border border-separator bg-card p-4 shadow-ios md:p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-label">Vollbild-Anzeigen</h2>
          <p className="mt-1 text-sm text-label-secondary">
            Geräte, die <code className="text-xs">/montageplan/vollbild</code> ohne Anmeldung
            öffnen, erscheinen hier zur Freigabe. Erst nach Freigabe sehen sie den Montageplan
            (ohne Arbeitszeiten).
          </p>
        </div>
        {pendingCount > 0 ? (
          <span className="rounded-full bg-amber-500/15 px-3 py-1 text-sm font-medium text-amber-700 dark:text-amber-300">
            {pendingCount} ausstehend
          </span>
        ) : null}
        <Button
          size="sm"
          variant="secondary"
          disabled={broadcastBusy}
          onClick={() => {
            setBroadcastBusy(true);
            void broadcastDisplayAppUpdate()
              .catch((err) => {
                setError(err instanceof Error ? err.message : "Broadcast fehlgeschlagen.");
              })
              .finally(() => setBroadcastBusy(false));
          }}
        >
          Anzeigen neu laden
        </Button>
      </div>

      {error ? <p className="mb-3 text-sm text-status-red">{error}</p> : null}

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-label-secondary">
          <Spinner className="h-4 w-4" />
          Geräte werden geladen …
        </div>
      ) : items.length === 0 ? (
        <p className="py-6 text-sm text-label-tertiary">Noch keine Geräte registriert.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((device) => {
            const shortId = device.device_id.slice(0, 8).toUpperCase();
            const isPending = device.status === "pending";
            const isApproved = device.status === "approved";
            const canReapprove =
              device.status === "revoked" || device.status === "rejected";
            return (
              <li
                key={device.id}
                className={
                  isPending
                    ? "rounded-ios border border-amber-500/35 bg-amber-500/[0.06] p-3"
                    : "rounded-ios border border-separator bg-fill-quaternary/20 p-3"
                }
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-label">{shortId}</span>
                      <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-xs text-label-secondary">
                        {statusLabel(device.status)}
                      </span>
                    </div>
                    {device.device_label ? (
                      <p className="text-sm text-label">{device.device_label}</p>
                    ) : null}
                    <p className="truncate text-xs text-label-tertiary">
                      {device.user_agent || "Unbekannter Browser"}
                    </p>
                    <p className="text-xs text-label-tertiary">
                      Angefragt: {formatWhen(device.requested_at)}
                      {device.last_seen_at ? ` · Zuletzt aktiv: ${formatWhen(device.last_seen_at)}` : ""}
                    </p>
                    <label className="mt-2 flex flex-wrap items-center gap-2 text-xs text-label-secondary">
                      <span>Theme:</span>
                      <select
                        className="rounded-ios border border-separator bg-card px-2 py-1 text-xs text-label"
                        value={device.theme_mode ?? "dark"}
                        disabled={themeBusyId === device.id}
                        onChange={(e) =>
                          void changeTheme(device.id, e.target.value as DisplayThemeMode)
                        }
                      >
                        {THEME_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="mt-2 space-y-2">
                      <label className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-label-secondary">
                        <span className="shrink-0">Größe:</span>
                        <input
                          type="range"
                          min={DISPLAY_UI_SCALE_MIN}
                          max={DISPLAY_UI_SCALE_MAX}
                          step={5}
                          value={normalizeDisplayUiScale(device.ui_scale ?? 100)}
                          disabled={scaleBusyId === device.id}
                          className="min-w-[120px] flex-1 accent-accent"
                          onChange={(e) =>
                            void changeScale(device.id, Number.parseInt(e.target.value, 10))
                          }
                        />
                        <span className="w-10 shrink-0 text-right font-mono text-label">
                          {normalizeDisplayUiScale(device.ui_scale ?? 100)}%
                        </span>
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {DISPLAY_UI_SCALE_PRESETS.map((preset) => (
                          <button
                            key={preset.value}
                            type="button"
                            disabled={scaleBusyId === device.id}
                            className="rounded-full border border-separator bg-card px-2 py-0.5 text-[11px] text-label-secondary transition hover:bg-fill-tertiary disabled:opacity-50"
                            onClick={() => void changeScale(device.id, preset.value)}
                          >
                            {preset.label} ({preset.value}%)
                          </button>
                        ))}
                      </div>
                      <p className="text-[11px] text-label-tertiary">
                        Schrift, Linien, Radien und Zellen – Änderung wird live auf dem Gerät übernommen.
                      </p>
                    </div>
                    {device.approved_by_display ? (
                      <p className="text-xs text-label-tertiary">
                        Freigegeben von {device.approved_by_display} ({formatWhen(device.approved_at)})
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    {isPending || canReapprove ? (
                      <>
                        <Button
                          size="sm"
                          disabled={busyId === device.id}
                          onClick={() => void runAction(device.id, "approve")}
                        >
                          Freigeben
                        </Button>
                        {isPending ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={busyId === device.id}
                            onClick={() => void runAction(device.id, "reject")}
                          >
                            Ablehnen
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                    {isApproved ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busyId === device.id}
                        onClick={() => void runAction(device.id, "revoke")}
                      >
                        Widerrufen
                      </Button>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
